import { useCallback, useEffect, useState } from 'react';
import type { MemberSummary } from '@starter/shared';
import { useProgramId } from '../../auth/AuthContext.js';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { Modal } from '../../components/Modal.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';

export function InstructorsPage() {
  const programId = useProgramId();
  const [members, setMembers] = useState<MemberSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .listMembers({ programId, role: 'INSTRUCTOR', page: 1, pageSize: 100 })
      .then((res) => setMembers(res.data))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load instructors'))
      .finally(() => setLoading(false));
  }, [programId]);

  useEffect(load, [load]);

  function openCreate() {
    setFullName('');
    setEmail('');
    setPassword('');
    setFormError(null);
    setModalOpen(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await api.createMember({
        programId,
        fullName,
        email,
        password,
        role: 'INSTRUCTOR',
      });
      setModalOpen(false);
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  async function toggleStatus(member: MemberSummary) {
    try {
      await api.updateMember(member.membershipId, {
        status: member.status === 'active' ? 'inactive' : 'active',
      });
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Update failed');
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Instructors"
        subtitle="Clinical faculty with review access"
        actions={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            Add instructor
          </button>
        }
      />

      {error && <ErrorBanner message={error} onRetry={load} />}

      {loading ? (
        <LoadingState message="Loading instructors…" />
      ) : (
        <div className="card">
          {members.length === 0 ? (
            <p className="text-muted">No instructors yet.</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.membershipId}>
                      <td>{m.fullName}</td>
                      <td>{m.email}</td>
                      <td>
                        <StatusBadge status={m.status} variant={m.status === 'active' ? 'success' : 'muted'} />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => void toggleStatus(m)}
                        >
                          {m.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Modal
        open={modalOpen}
        title="Add instructor"
        onClose={() => setModalOpen(false)}
        footer={
          <div className="form-actions">
            <button type="button" className="btn btn--ghost" onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" form="instructor-form" className="btn btn--primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create'}
            </button>
          </div>
        }
      >
        <form id="instructor-form" onSubmit={handleSubmit}>
          {formError && <div className="banner banner--error" role="alert">{formError}</div>}
          <label className="field">
            <span className="field__label">Full name</span>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </label>
          <label className="field">
            <span className="field__label">Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label className="field">
            <span className="field__label">Temporary password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
            />
          </label>
        </form>
      </Modal>
    </div>
  );
}
